import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import toast from 'components/partials/toast/toast';

import {
  useAidModel,
  useCreateAidModel,
  useUpdateAidModel,
  useAidSourceTables,
  useAidModelDataset,
  useCreateAidModelDataset,
} from './use-aid-model-details';

import { useCurrentUser } from 'state/current-user/current-user.thunk';

import { DATASET_STATUSES } from '../aid-studio-details.constants';

interface UseAidStudioFormParams {
  clientId: string | number;
  modelId: string | number | undefined;
}

interface AidStudioFormValues {
  name: string;
  description: string;
  entryTerm: number | null;
  enrollmentType: string;
  sourceTableName: string;
  status: string;
}

const DATASET_POLL_INTERVAL = 20000;
const DATASET_TOAST_ID = 'dataset-creation-progress';

const DEFAULT_VALUES: AidStudioFormValues = {
  name: '',
  description: '',
  entryTerm: null,
  enrollmentType: 'FIRST_YEAR',
  sourceTableName: '',
  status: 'DRAFT',
};

export const useAidStudioForm = ({
  clientId,
  modelId,
}: UseAidStudioFormParams) => {
  const navigate = useNavigate();
  const isNewModel = !modelId;

  const [isSaving, setIsSaving] = useState(false);
  const [recreateDataset, setRecreateDataset] = useState(false);
  const [isDatasetPolling, setIsDatasetPolling] = useState(false);
  const isFormInitializedRef = useRef(false);

  const currentUser = useCurrentUser() as unknown as {
    data?: { oktaUserId?: string };
  };
  const userId = currentUser?.data?.oktaUserId;

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isValid, isDirty },
  } = useForm<AidStudioFormValues>({
    mode: 'onChange',
    defaultValues: DEFAULT_VALUES,
  });

  const aidModelQuery = useAidModel({
    userId: userId ?? '',
    clientId,
    modelId: modelId ?? '',
    enabled: !isNewModel,
  });
  const modelData = aidModelQuery.data as
    | {
        name?: string;
        description?: string;
        entryTerm?: number | null;
        enrollmentType?: string;
        status?: string;
      }
    | undefined;
  const modelLoading = aidModelQuery.isLoading;
  const modelError = aidModelQuery.isError;

  const sourceTablesQuery = useAidSourceTables({
    userId: userId ?? '',
    enabled: !!userId,
  });
  const sourceTables = sourceTablesQuery.data as
    | { name: string }[]
    | undefined;
  const sourceTablesLoading = sourceTablesQuery.isLoading;
  const sourceTablesFetching = sourceTablesQuery.isFetching;

  const datasetQuery = useAidModelDataset({
    userId: userId ?? '',
    modelId: modelId ?? '',
    enabled: !isNewModel && !!modelId && !!userId,
    refetchInterval: isDatasetPolling ? DATASET_POLL_INTERVAL : false,
  });
  const datasetData = datasetQuery.data as
    | { status?: string; sourceTableName?: string }
    | undefined;

  useEffect(() => {
    const status = datasetData?.status;

    if (status === DATASET_STATUSES.IN_PROGRESS) {
      if (!isDatasetPolling) {
        setIsDatasetPolling(true);
        toast.info({
          title: 'Dataset recreation started',
          message: `We'll notify you once it's ready`,
          toastId: DATASET_TOAST_ID,
          autoClose: 5000,
        });
      }

      return;
    }

    if (!isDatasetPolling) return;

    toast.dismiss(DATASET_TOAST_ID);

    if (status === DATASET_STATUSES.CREATED) {
      toast.success({
        title: 'Dataset recreated successfully',
        message: 'Your dataset is ready to use',
      });
    } else if (status === DATASET_STATUSES.FAILED) {
      toast.error({
        title: 'Dataset recreation failed',
        message: 'Something went wrong. Please try again',
      });
    }

    setIsDatasetPolling(false);
  }, [datasetData?.status, isDatasetPolling]);

  useEffect(() => () => toast.dismiss(DATASET_TOAST_ID), []);

  const createModel = useCreateAidModel();
  const updateModel = useUpdateAidModel();
  const createDataset = useCreateAidModelDataset();

  const sourceTablesOptions = useMemo(() => {
    if (!sourceTables) return [];

    const options = sourceTables.map((table) => ({
      value: table.name,
      label: table.name,
    }));

    const savedTableName = datasetData?.sourceTableName;

    if (
      !isNewModel &&
      savedTableName &&
      !options.some((opt) => opt.value === savedTableName)
    ) {
      options.unshift({ value: savedTableName, label: savedTableName });
    }

    return options;
  }, [sourceTables, isNewModel, datasetData?.sourceTableName]);

  useEffect(() => {
    if (
      modelData &&
      !isNewModel &&
      (!isDatasetPolling || !isFormInitializedRef.current)
    ) {
      isFormInitializedRef.current = true;
      reset({
        name: modelData.name || '',
        description: modelData.description || '',
        entryTerm: modelData.entryTerm ?? null,
        enrollmentType: modelData.enrollmentType || 'FIRST_YEAR',
        sourceTableName: datasetData?.sourceTableName || '',
        status: modelData.status || 'DRAFT',
      });
    }
  }, [modelData, datasetData, isNewModel, reset, isDatasetPolling]);

  const handleRecreateDatasetChange = (checked: boolean): void => {
    setRecreateDataset(checked);

    if (!checked) {
      setValue('sourceTableName', datasetData?.sourceTableName || '', {
        shouldDirty: true,
      });
    }
  };

  const triggerDatasetCreation = async (
    targetModelId: string | number,
    sourceTableName: string
  ): Promise<void> => {
    await createDataset.mutateAsync({
      userId: userId ?? '',
      modelId: targetModelId,
      sourceTableName,
    });
  };

  const handleSave = async (
    formData: AidStudioFormValues,
    navigateAfter = false
  ): Promise<void> => {
    setIsSaving(true);

    try {
      const modelPayload = {
        name: formData.name,
        description: formData.description || null,
        entryTerm: formData.entryTerm,
        enrollmentType: formData.enrollmentType,
        status: formData.status || 'DRAFT',
      };

      let savedModelId: string | number | undefined = modelId;

      if (isNewModel) {
        const result = (await createModel.mutateAsync({
          userId: userId ?? '',
          clientId,
          data: modelPayload,
        })) as { id: string | number };

        savedModelId = result.id;

        toast.success({
          title: 'Aid model created',
          message: `You've successfully created ${formData.name}`,
        });

        if (formData.sourceTableName && savedModelId != null) {
          try {
            await triggerDatasetCreation(
              savedModelId,
              formData.sourceTableName
            );
          } catch {
            toast.error({
              title: 'Dataset creation failed',
              message:
                'The model was saved, but dataset creation could not be started',
            });
          }
        }

        navigate(`/admin/clients/${clientId}/aid-studio/${savedModelId}`, {
          replace: true,
        });
      } else {
        await updateModel.mutateAsync({
          userId: userId ?? '',
          clientId,
          modelId: modelId ?? '',
          data: modelPayload,
        });

        toast.success({
          title: 'Aid model updated',
          message: `You've successfully updated ${formData.name}`,
        });

        if (recreateDataset && formData.sourceTableName && modelId != null) {
          try {
            await triggerDatasetCreation(modelId, formData.sourceTableName);
            setRecreateDataset(false);
          } catch {
            toast.error({
              title: 'Dataset recreation failed',
              message:
                'The model was saved, but dataset recreation could not be started',
            });
          }
        }

        reset({
          name: formData.name || '',
          description: formData.description || '',
          entryTerm: formData.entryTerm ?? null,
          enrollmentType: formData.enrollmentType || 'FIRST_YEAR',
          sourceTableName: formData.sourceTableName || '',
          status: formData.status || 'DRAFT',
        });
      }

      if (navigateAfter) {
        navigate(`/admin/clients/${clientId}/aid-studio`);
      }
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } } };

      toast.error({
        title: 'Error saving aid model',
        message: err?.response?.data?.message || 'An error occurred',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = (): void => {
    navigate(`/admin/clients/${clientId}/aid-studio`);
  };

  const handleDiscard = (): void => {
    setRecreateDataset(false);

    if (modelData && !isNewModel) {
      reset({
        name: modelData.name || '',
        description: modelData.description || '',
        entryTerm: modelData.entryTerm ?? null,
        enrollmentType: modelData.enrollmentType || 'FIRST_YEAR',
        sourceTableName: datasetData?.sourceTableName || '',
        status: modelData.status || 'DRAFT',
      });
    } else {
      reset(DEFAULT_VALUES);
    }
  };

  return {
    control,
    errors,
    isValid,
    isDirty,
    handleSubmit,
    isNewModel,
    isSaving,
    isLoading: !isNewModel && modelLoading,
    isError: modelError,
    modelData,
    datasetData,
    sourceTablesOptions,
    sourceTablesLoading,
    sourceTablesFetching,
    recreateDataset,
    handleRecreateDatasetChange,
    handleSave,
    handleCancel,
    handleDiscard,
  };
};
